/* Service-worker registration + update plumbing (Session 25.5).
 *
 * Registers /service-worker.js (served from /public). When a new SW is
 * detected and finishes installing, we expose an `onUpdateReady` hook
 * so the UI can surface a "Reload to update" toast. The user opts in
 * by clicking the toast — we then post `SKIP_WAITING` to the waiting
 * worker and reload once `controllerchange` fires.
 *
 * We deliberately do NOT auto-reload behind the user's back: a kitchen
 * tablet mid-service shouldn't lose its place because we shipped a
 * minor patch. The toast stays visible until acknowledged.
 *
 * Historical note: an earlier version of this file unregistered the SW
 * and wiped all caches on emergent preview hosts to escape a refresh
 * loop. That protection is no longer needed because the SW no longer
 * calls `self.skipWaiting()` on install, so preview-server rebuilds
 * cannot trigger the loop anymore. Disabling the SW on those hosts
 * also disabled offline capability for testing — that is why the
 * PWA could not boot offline in Session 25.
 */
const SW_URL = "/service-worker.js";

// Session 27.48 — Client-side build tag. Exposed globally so the
// operator can verify the running version in DevTools console via
// `window.__NFCS_VERSION` or the DevTools help panel. If preview
// reports one value and production a different one, the production
// device is running stale JS and needs a reload.
export const NFCS_BUILD_VERSION = "27.50-2026-01-menu-search";
if (typeof window !== "undefined") {
  try { window.__NFCS_VERSION = NFCS_BUILD_VERSION; } catch (_) { /* noop */ }
}

// Guard so multiple registerServiceWorker() calls in a session never
// register twice (React 18 StrictMode + hot reload would otherwise
// double-register in some corner cases).
let _registered = false;

// Session 67 — `registration.update()` returns a promise that REJECTS when
// the SW script cannot be fetched (iOS Safari suspending the tab, a flaky
// hotspot…). A bare try/catch never caught that async rejection, so every
// such moment was logged as an unhandled "Script …/service-worker.js load
// failed" device error. Swallow it: the next poll simply tries again.
function safeSwUpdate(registration) {
  try {
    const p = registration && registration.update && registration.update();
    if (p && typeof p.catch === "function") p.catch(() => { /* retried on the next poll */ });
  } catch (_) { /* noop */ }
}

export function registerServiceWorker({ onUpdateReady } = {}) {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  if (_registered) return;
  // Never register the browser Service Worker inside the Capacitor
  // shell — the WebView already ships the built assets, and layering
  // an SW cache on top of that would double-cache resources and can
  // confuse update semantics against Capgo live updates.
  if (_isCapacitorShell()) return;
  _registered = true;

  // Skip in CRA hot-reload dev — the dev server serves modules with
  // no-cache headers and the SW would confuse HMR. Production/preview
  // hosts (no port or port 443) register.
  const isLocalDev = ["localhost", "127.0.0.1"].includes(window.location.hostname);
  if (isLocalDev && window.location.port && window.location.port !== "443") {
    return;
  }

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register(SW_URL)
      .then((registration) => {
        // Session 27.41 — Immediately check for a newer SW on register.
        safeSwUpdate(registration);

        // Session 27.48 — Graceful auto-reload on SW controller
        // change.
        //
        // The new SW (v12+) calls `self.skipWaiting()` on install and
        // `clients.claim()` on activate. As soon as a redeploy lands,
        // the new SW takes control of this page and the browser fires
        // `controllerchange`. We reload once (guarded by a flag so we
        // don't loop) to swap the running JS for the fresh bundle.
        //
        // If the user has an installed PWA that's currently open, this
        // happens ~instantly on any redeploy — combined with the SW's
        // aggressive skipWaiting they no longer need to "close tabs and
        // reopen" or "Force Clear Cache" ever again.
        try {
          let _swReloaded = false;
          navigator.serviceWorker.addEventListener("controllerchange", () => {
            if (_swReloaded) return;
            _swReloaded = true;
            try { window.location.reload(); } catch (_) { /* noop */ }
          });
        } catch (_) { /* noop */ }

        // Fires when a brand-new SW is discovered while an old one
        // still controls the page — this is a TRUE update.
        const armUpdateChannel = (worker) => {
          if (!worker) return;
          worker.addEventListener("statechange", () => {
            if (
              worker.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              if (typeof onUpdateReady === "function") onUpdateReady(registration);
            }
          });
        };

        if (registration.waiting && navigator.serviceWorker.controller) {
          // Update was found before this listener attached (e.g. user
          // returned to a tab where the new SW had already installed).
          if (typeof onUpdateReady === "function") onUpdateReady(registration);
        }
        registration.addEventListener("updatefound", () => {
          armUpdateChannel(registration.installing);
        });

        // Session 27.23 — More aggressive update polling. Previous
        // 6h cadence meant redeploys took hours to reach devices that
        // never fully closed the tab. Now we:
        //   • Poll every 30 minutes (12x faster than before).
        //   • Also check on `visibilitychange` when the tab becomes
        //     visible AND the last check was > 5 minutes ago. This
        //     was previously removed for "firing too aggressively",
        //     but the throttle here keeps it to at most once every
        //     5 minutes so tablets that switch apps constantly won't
        //     hammer the SW.
        // Combined, this gives operators a working "Reload" toast
        // within minutes of a redeploy without spamming the network.
        const pollId = window.setInterval(() => {
          safeSwUpdate(registration);
        }, 30 * 60 * 1000);
        window.__nfcsSwPoll = pollId;

        let lastUpdateCheck = Date.now();
        const throttledUpdateCheck = () => {
          if (document.visibilityState !== "visible") return;
          if (Date.now() - lastUpdateCheck < 5 * 60 * 1000) return;
          lastUpdateCheck = Date.now();
          safeSwUpdate(registration);
        };
        document.addEventListener("visibilitychange", throttledUpdateCheck);

        // The SW no longer forces itself active — the user must
        // explicitly opt in via the "Update ready" toast. When they
        // do, applyServiceWorkerUpdate() posts SKIP_WAITING to the
        // waiting worker; that triggers controllerchange, and we
        // reload exactly once to pick up the new shell.
        let reloaded = false;
        navigator.serviceWorker.addEventListener("controllerchange", () => {
          if (reloaded) return;
          // Only reload if we KNOW the user asked for it. Otherwise a
          // fresh install (first visit ever) also fires controllerchange
          // and we'd trigger an unnecessary reload.
          if (window.sessionStorage.getItem("nfcs:sw:update-armed") !== "1") return;
          reloaded = true;
          window.sessionStorage.removeItem("nfcs:sw:update-armed");
          window.location.reload();
        });
      })
      .catch((err) => {
        /* Registration can fail in private-browsing or behind certain
         * corporate proxies. Not fatal — the app degrades to a normal
         * tab without offline support. */
        console.warn("[pwa] SW registration failed:", err?.message || err);
      });
  });
}

// Called by the update toast when the user clicks "Reload to update".
export function applyServiceWorkerUpdate(registration) {
  try {
    // Arm the controllerchange listener so it knows this reload is
    // user-intentional (see registerServiceWorker).
    window.sessionStorage.setItem("nfcs:sw:update-armed", "1");
    const waiting = registration && registration.waiting;
    if (waiting) {
      waiting.postMessage({ type: "SKIP_WAITING" });
    } else if (navigator.serviceWorker.controller) {
      // No waiting worker? Force reload anyway so the user gets a fresh shell.
      window.location.reload();
    }
  } catch (_) {
    window.location.reload();
  }
}

// ─────────────────────────────────────────────────────────────────────
// Install-prompt plumbing
// ─────────────────────────────────────────────────────────────────────
// Chromium-based browsers (Chrome, Edge, Samsung Internet) fire a
// `beforeinstallprompt` event when the PWA install criteria are met.
// The event is fire-once and the prompt can only be shown inside a
// user gesture later. We capture it module-level so any component
// (e.g. an "Install" button or the /install landing page) can trigger
// it on demand. iOS Safari does NOT emit this event — installation
// there is purely manual via Share → Add to Home Screen.

let _deferredInstallPrompt = null;
let _installPromptInstalled = false;

/** True when the app is running as a Capacitor native shell. Kept
 *  duplicated here (rather than importing from ./platform) so this
 *  module stays free of any Capacitor SDK code — the check needs to
 *  work even if @capacitor/core isn't installed at build time. */
function _isCapacitorShell() {
  return !!(typeof window !== "undefined"
    && window.Capacitor
    && typeof window.Capacitor.isNativePlatform === "function"
    && window.Capacitor.isNativePlatform());
}

if (typeof window !== "undefined" && !_isCapacitorShell()) {
  // Only wire the browser-PWA install prompt in the browser build.
  // Inside the Capacitor APK the browser install prompt has no
  // meaning — the app is already "installed" as a native shell.
  window.addEventListener("beforeinstallprompt", (event) => {
    // Stop Chrome from showing its own mini-infobar — we want full
    // control over when to surface the prompt.
    event.preventDefault();
    _deferredInstallPrompt = event;
    try {
      window.dispatchEvent(new CustomEvent("nfcs:install-available"));
    } catch (_) {
      /* noop */
    }
  });
  window.addEventListener("appinstalled", () => {
    _installPromptInstalled = true;
    _deferredInstallPrompt = null;
    try {
      window.dispatchEvent(new CustomEvent("nfcs:installed"));
    } catch (_) {
      /* noop */
    }
  });
}

export function isInstallPromptAvailable() {
  return Boolean(_deferredInstallPrompt);
}

export function hasBeenInstalled() {
  return _installPromptInstalled;
}

// Returns a promise that resolves to "accepted" | "dismissed" | "unavailable".
// MUST be called inside a real user gesture (e.g. a click handler).
export async function triggerInstallPrompt() {
  if (!_deferredInstallPrompt) return "unavailable";
  try {
    const promptEvent = _deferredInstallPrompt;
    _deferredInstallPrompt = null; // single-use; the OS guarantees this
    await promptEvent.prompt();
    const result = await promptEvent.userChoice;
    return result && result.outcome ? result.outcome : "dismissed";
  } catch (_) {
    return "dismissed";
  }
}

// Lightweight platform detection so the install page can show the
// right copy ("Tap Install" vs "Tap Share → Add to Home Screen").
export function detectInstallPlatform() {
  if (typeof window === "undefined") return "other";
  const ua = (navigator.userAgent || "").toLowerCase();
  const isStandalone = (
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    window.navigator.standalone === true
  );
  if (isStandalone) return "installed";
  // iPad with desktop-class Safari user-agent reports as Mac — fall
  // back to touch detection in that case.
  const isIpad = /macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1;
  if (/iphone|ipod/.test(ua) || /ipad/.test(ua) || isIpad) return "ios";
  if (/android/.test(ua)) return "android";
  if (/windows|macintosh|linux|cros/.test(ua)) return "desktop";
  return "other";
}

// ─────────────────────────────────────────────────────────────────────
// Offline readiness helpers used by Install/Setup + Local Status
// ─────────────────────────────────────────────────────────────────────

/**
 * Best-effort proof that the SW has cached the app shell so the PWA
 * can boot offline. Checks (a) that a SW is currently controlling the
 * page, (b) that a "/" or "/index.html" response can be produced from
 * CacheStorage without a network round-trip.
 */
export async function isAppShellCached() {
  try {
    if (!("caches" in window)) return false;
    const hit =
      (await caches.match("/index.html", { ignoreSearch: true })) ||
      (await caches.match("/", { ignoreSearch: true }));
    return !!hit;
  } catch (_) {
    return false;
  }
}

/** Is a SW currently controlling this page? */
export function isServiceWorkerActive() {
  try {
    return !!(navigator.serviceWorker && navigator.serviceWorker.controller);
  } catch (_) {
    return false;
  }
}
